import { Injectable } from '@angular/core';
import { Workbook } from 'exceljs';
import Swal from 'sweetalert2';
import * as moment from 'moment-timezone';
import { GasprecioService } from './gasprecio.service';

/** Periodo visible de la tabla que se exporta (YYYY-MM-DD). */
export interface ExportRange {
  start: string;
  end: string;
}

/** Columna del Excel: encabezado, ancho y formato numérico opcional. */
export interface PipesExcelColumn {
  header: string;
  width: number;
  numFmt?: string;
}

/** Celda del Excel: texto, número, fecha o vacía. */
export type PipesExcelCell = string | number | Date | null;

/** Sección del Formato de Cambios de MOBICO: banda de color con su título,
 *  encabezados propios y sus filas (una vacía si no hay). */
export interface PipesExcelSection {
  title: string;
  /** ARGB del relleno de la banda. */
  color: string;
  columns: PipesExcelColumn[];
  rows: PipesExcelCell[][];
}

export interface PipesExcelSectionsSpec {
  sheet: string;
  title: string;
  fileName: string;
  sections: PipesExcelSection[];
}

/** Todo lo que hace falta para armar y descargar un Excel del flujo de pipas. */
export interface PipesExcelSpec {
  sheet: string;
  title: string;
  fileName: string;
  columns: PipesExcelColumn[];
  rows: PipesExcelCell[][];
}

export type PipesToastIcon = 'success' | 'error' | 'info';

const MAX_PROMPT_DAYS = 40;
const THIN_BORDER = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } } as const;

/**
 * Exportación a Excel del flujo de pipas (Solicitudes, Cronograma, Sugerencias y
 * el panel del admin): el prompt del día, el armado del libro (título, encabezado,
 * bordes, anchos, formatos) y el toast de resultado viven aquí, una sola vez.
 * Los componentes solo describen sus columnas y sus filas.
 */
@Injectable({ providedIn: 'root' })
export class PipesExportService {
  constructor(private gasprecioService: GasprecioService) {}

  /** Día a exportar: ventana con las fechas del periodo (o las que se pasen) y
   *  "Todos los días". '' = todos · null = canceló. */
  async pickDay(title: string, range: ExportRange, dates: string[] = []): Promise<string | null> {
    const options: Record<string, string> = { '': 'Todos los días' };
    const list = dates.length ? dates : this.daysOf(range);
    list.forEach((d) => { options[d] = this.gasprecioService.capitalizeFirstLetter(moment(d).format('dddd DD [de] MMMM')); });
    const r = await Swal.fire({
      title,
      text: 'Elige el día que quieres descargar.',
      input: 'select',
      inputOptions: options,
      inputValue: '',
      showCancelButton: true,
      confirmButtonText: 'Descargar',
      cancelButtonText: 'Cancelar',
      // Look del portal (modales del flujo), no el default de SweetAlert: clases en styles.scss.
      customClass: {
        popup: 'pipes-prompt', title: 'pipes-prompt__title', htmlContainer: 'pipes-prompt__text', input: 'pipes-prompt__select',
        actions: 'pipes-prompt__actions', confirmButton: 'pipes-prompt__ok', cancelButton: 'pipes-prompt__cancel',
      },
      buttonsStyling: false,
      width: 440,
    });
    return r.isConfirmed ? String(r.value ?? '') : null;
  }

  /** "Lunes 12 de octubre 2026" o "05/10/2026 – 18/10/2026" para el título del libro. */
  dayLabel(day: string, range: ExportRange): string {
    return day
      ? this.gasprecioService.capitalizeFirstLetter(moment(day).format('dddd DD [de] MMMM YYYY'))
      : `${moment(range.start).format('DD/MM/YYYY')} – ${moment(range.end).format('DD/MM/YYYY')}`;
  }

  /** Sufijo del nombre de archivo: el día o el rango. */
  daySuffix(day: string, range: ExportRange): string {
    return day || `${moment(range.start).format('YYYY-MM-DD')}_${moment(range.end).format('YYYY-MM-DD')}`;
  }

  /** ¿La fecha (UTC) cae en el día elegido? Sin día elegido, siempre sí. */
  sameDay(value: string | Date | null | undefined, day: string): boolean {
    return !day || (!!value && moment.utc(value).format('YYYY-MM-DD') === day);
  }

  /** DD/MM/YYYY en el huso dado ('-06:00' para fechas con hora de MX, '+00:00' para fechas-día UTC). */
  dmy(value: string | Date | null | undefined, utcOffset: string): string {
    return value ? moment(value).utcOffset(utcOffset).format('DD/MM/YYYY') : '';
  }

  /** Arma el libro y lo descarga; avisa con el toast del flujo. */
  async download(spec: PipesExcelSpec): Promise<void> {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet(spec.sheet);
    worksheet.addRow([spec.title]);
    worksheet.addRow([]);
    this.gasprecioService.addHeaderRow(worksheet, spec.columns.map((c) => c.header));
    spec.rows.forEach((cells) => {
      const row = worksheet.addRow(cells);
      spec.columns.forEach((c, i) => {
        const cell = row.getCell(i + 1);
        cell.border = THIN_BORDER as any;
        if (c.numFmt) { cell.numFmt = c.numFmt; }
      });
    });
    spec.columns.forEach((c, i) => { worksheet.getColumn(i + 1).width = c.width; });
    await this.save(workbook, spec.fileName);
  }

  /** Libro con el formato de MOBICO: encabezados en la fila 1 (sin título), como
   *  el Excel que ellos arman a mano («MIERCOLES 14 DE OCTUBRE.xlsx»). */
  async downloadPlain(spec: PipesExcelSpec): Promise<void> {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet(spec.sheet);
    const header = worksheet.addRow(spec.columns.map((c) => c.header));
    header.eachCell((cell: any) => {
      cell.font = { bold: true };
      cell.alignment = { horizontal: 'center' };
      cell.border = THIN_BORDER as any;
    });
    spec.rows.forEach((cells) => {
      const row = worksheet.addRow(cells);
      spec.columns.forEach((c, i) => {
        const cell = row.getCell(i + 1);
        cell.border = THIN_BORDER as any;
        if (c.numFmt) { cell.numFmt = c.numFmt; }
      });
    });
    spec.columns.forEach((c, i) => { worksheet.getColumn(i + 1).width = c.width; });
    await this.save(workbook, spec.fileName);
  }

  /** Formato de Cambios de MOBICO: una banda de color por tipo (Adición ·
   *  Cancelación · Cambio de fecha · de capacidad · de producto), cada una con
   *  sus encabezados y sus filas; las vacías llevan una fila en blanco, como en
   *  el formato original. */
  async downloadSections(spec: PipesExcelSectionsSpec): Promise<void> {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet(spec.sheet);
    const titleRow = worksheet.addRow([spec.title]);
    titleRow.getCell(1).font = { bold: true, size: 16, color: { argb: 'FF1F4E9C' } };
    worksheet.addRow([]);
    let maxCols = 0;
    spec.sections.forEach((section) => {
      const n = section.columns.length;
      maxCols = Math.max(maxCols, n);
      const band = worksheet.addRow([section.title]);
      worksheet.mergeCells(band.number, 1, band.number, n);
      for (let i = 1; i <= n; i++) {
        const cell = band.getCell(i);
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: section.color } } as any;
        cell.font = { bold: true };
        cell.alignment = { horizontal: 'center' };
        cell.border = THIN_BORDER as any;
      }
      const header = worksheet.addRow(section.columns.map((c) => c.header));
      header.eachCell({ includeEmpty: true }, (cell: any, col: number) => {
        if (col > n) { return; }
        cell.font = { bold: true };
        cell.alignment = { horizontal: 'center' };
        cell.border = THIN_BORDER as any;
      });
      const rows = section.rows.length ? section.rows : [section.columns.map(() => null)];
      rows.forEach((cells) => {
        const row = worksheet.addRow(cells);
        section.columns.forEach((c, i) => {
          const cell = row.getCell(i + 1);
          cell.border = THIN_BORDER as any;
          cell.alignment = { horizontal: 'center' };
          if (c.numFmt) { cell.numFmt = c.numFmt; }
        });
      });
      worksheet.addRow([]);
    });
    // Ancho por columna: el mayor pedido entre las secciones.
    for (let i = 0; i < maxCols; i++) {
      worksheet.getColumn(i + 1).width = Math.max(...spec.sections.map((s) => s.columns[i]?.width || 0), 12);
    }
    await this.save(workbook, spec.fileName);
  }

  private async save(workbook: Workbook, fileName: string): Promise<void> {
    try {
      await this.gasprecioService.generateExcelFile(workbook, fileName);
      this.showToast('success', 'Excel descargado', `<div class="pipes-toast__note">${this.esc(fileName)}</div>`);
    } catch (e) {
      this.showToast('error', 'No se pudo generar el Excel');
    }
  }

  /** Aviso "no hay filas para ese día" con el look del portal. */
  showEmpty(title: string, text: string): void {
    Swal.fire({ icon: 'info', title, text, confirmButtonColor: '#00e07b' });
  }

  /** Toast del flujo de pipas (arriba a la derecha, 7 s, look .pipes-toast). */
  showToast(icon: PipesToastIcon, title: string, html?: string): void {
    const iconHtml = {
      success: '<i class="fa fa-check" aria-hidden="true"></i>',
      error: '<i class="fa fa-times" aria-hidden="true"></i>',
      info: '<i class="fa fa-info" aria-hidden="true"></i>',
    }[icon];
    Swal.fire({
      toast: true,
      position: 'top-end',
      icon,
      iconHtml,
      title,
      html,
      // 490 acompaña la escala grande del look .pipes-toast; con menos, el
      // detalle se partía en dos líneas. Mismo ancho para todos los toasts del flujo.
      width: html ? 490 : undefined,
      showConfirmButton: false,
      showCloseButton: true,
      timer: 7000,
      timerProgressBar: true,
      customClass: { popup: `pipes-toast pipes-toast--${icon}` },
    });
  }

  /** Chip soft-tint de tipo (Adición, Cambio de fecha…) para el html de un toast; color por whitelist. */
  typeChipHtml(kind: string, label: string, count?: number): string {
    const safe = ['addition', 'cancel', 'date', 'capacity', 'product'].includes(kind) ? kind : '';
    const suffix = count && count > 1 ? ` <b>×${count}</b>` : '';
    return `<span class="pipes-toast__chip ${safe ? `pipes-toast__chip--${safe}` : ''}">${this.esc(label)}${suffix}</span>`;
  }

  /** Badge de producto junto al tipo (Edey 20-oct), colores del flujo MOBICO en tinte suave; vacío sin producto. */
  productChipHtml(product: string, label: string, count?: number): string {
    if (!product) { return ''; }
    const safe = ['regular', 'premium', 'diesel'].includes(product) ? product : '';
    const suffix = count && count > 1 ? ` <b>×${count}</b>` : '';
    return `<span class="pipes-toast__chip pipes-toast__chip--prod ${safe ? `pipes-toast__chip--${safe}` : ''}">${this.esc(label)}${suffix}</span>`;
  }

  /**
   * Pares tipo + producto agrupados con su conteo («Adición · Extra ×3»), del más cargado al menos. Cada item trae su
   * tipo (kind/label) y producto (product/productLabel).
   */
  typeProductChipsHtml(items: { kind: string; label: string; product: string; productLabel: string }[]): string {
    const groups = new Map<string, { item: typeof items[number]; count: number }>();
    items.forEach((it) => {
      const key = `${it.kind}|${it.product}`;
      const g = groups.get(key) || { item: it, count: 0 };
      g.count += 1;
      groups.set(key, g);
    });
    return [...groups.values()]
      .sort((a, b) => b.count - a.count)
      .map(({ item, count }) => `<span class="pipes-toast__pair">${this.typeChipHtml(item.kind, item.label)}${this.productChipHtml(item.product, item.productLabel, count)}</span>`)
      .join('');
  }

  /** Escapa valores de datos que van dentro del html de un toast. */
  esc(v: unknown): string {
    return String(v ?? '').replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\'': '&#39;' }[c] as string));
  }

  private daysOf(range: ExportRange): string[] {
    const out: string[] = [];
    const end = moment(range.end);
    for (const d = moment(range.start); d.isSameOrBefore(end, 'day') && out.length <= MAX_PROMPT_DAYS; d.add(1, 'day')) {
      out.push(d.format('YYYY-MM-DD'));
    }
    return out;
  }
}
